import React, { useCallback, useEffect, useState } from "react";
import { BellRing, Loader2, MailCheck, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Who is waiting for a sponsor to become available.
 *
 * This panel is a list, not a mailer. No email is sent from Sponsor Me today -
 * there is no email provider configured - so the only actions here are "mark as
 * contacted" (a note, after you have reached out yourself) and "delete".
 *
 * The data is health-adjacent (an email address plus a recovery pathway), so it
 * lives nowhere else: it is admin-only, never public, never in analytics, and
 * every row shows the exact consent sentence the person agreed to and when.
 */
const FILTERS = [
  { key: "active", label: "Active" },
  { key: "unsubscribed", label: "Unsubscribed" },
];

const PATHWAY_LABELS = {
  ANY: "Any pathway",
  AA: "AA",
  NA: "NA",
  SMART: "SMART Recovery",
  CMA: "CMA",
  OTHER: "Other pathway",
};

export default function AdminAvailabilityAlerts() {
  const [status, setStatus] = useState("active");
  const [items, setItems] = useState([]);
  const [byPathway, setByPathway] = useState({});
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/availability-alerts?status=${status}`);
      setItems(data?.alerts || []);
      setByPathway(data?.active_by_pathway || {});
      setNote(data?.sending_note || "");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id, kind) => {
    setBusy(id);
    try {
      if (kind === "notified") {
        await api.post(`/admin/availability-alerts/${id}/mark-notified`);
        toast.success("Marked as contacted.");
      } else {
        await api.delete(`/admin/availability-alerts/${id}`);
        toast.success("Deleted.");
      }
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <section data-testid="admin-availability-alerts" className="space-y-4">
      <div className="rounded-2xl border border-[#E6D3A3] bg-[#FDF6E7] p-4">
        <p className="flex items-center gap-2 text-sm font-bold text-[#6B4A22]">
          <BellRing className="w-4 h-4 shrink-0" aria-hidden="true" />
          Nothing here has been emailed
        </p>
        <p data-testid="admin-alerts-sending-note" className="mt-1 text-sm text-[#6B4A22] leading-6">
          {note ||
            "No emails have been sent from this list. Sponsor Me has no email provider configured yet, so sending is a manual, admin-approved step once one is added."}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setStatus(f.key)}
            data-testid={`admin-alerts-filter-${f.key}`}
            className={`min-h-[44px] rounded-xl border px-4 text-sm font-semibold transition-colors duration-150 ${
              status === f.key
                ? "border-[#1F6F78] bg-[#D7EEF0] text-[#134B52]"
                : "border-[#E3DED4] bg-white text-[#4B5A60] hover:bg-[#F2EFE8]"
            }`}
          >
            {f.label}
          </button>
        ))}
        <span className="text-sm text-[#4B5A60]" data-testid="admin-alerts-pathway-counts">
          {Object.keys(byPathway).length
            ? Object.entries(byPathway)
                .map(([k, v]) => `${PATHWAY_LABELS[k] || k}: ${v}`)
                .join(" · ")
            : "No active requests"}
        </span>
      </div>

      {loading ? (
        <p className="text-sm text-[#4B5A60]">Loading requests...</p>
      ) : items.length === 0 ? (
        <p data-testid="admin-alerts-empty" className="text-sm text-[#4B5A60]">
          Nobody has asked to be notified yet.
        </p>
      ) : (
        <ul className="space-y-3">
          {items.map((a) => (
            <li
              key={a.id}
              data-testid={`admin-alert-${a.id}`}
              className="rounded-2xl border border-[#E3DED4] bg-white p-4"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-[#1F2A2E] break-all">{a.email}</p>
                  <p className="mt-0.5 text-sm text-[#4B5A60]">
                    {PATHWAY_LABELS[a.pathway] || a.pathway}
                    {a.is_test_email ? " · test email" : ""}
                    {a.notified_at ? " · already contacted" : ""}
                  </p>
                  <p className="mt-2 text-xs text-[#7A868B] leading-5">
                    Consented {a.consent_at ? new Date(a.consent_at).toLocaleString() : "-"}:
                    &ldquo;{a.consent_wording}&rdquo;
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => act(a.id, "notified")}
                    disabled={busy === a.id}
                    data-testid={`admin-alert-notified-${a.id}`}
                    className="h-11 bg-white border-[#E3DED4]"
                  >
                    {busy === a.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                    ) : (
                      <>
                        <MailCheck className="w-4 h-4 mr-1.5" aria-hidden="true" />
                        Mark contacted
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => act(a.id, "delete")}
                    disabled={busy === a.id}
                    data-testid={`admin-alert-delete-${a.id}`}
                    className="h-11 bg-white border-[#E3DED4] text-[#B42318] hover:bg-[#FBE6E4]"
                  >
                    <Trash2 className="w-4 h-4" aria-hidden="true" />
                    <span className="sr-only">Delete request</span>
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
